/**
 * SG PreSet — Withdraw Secretary (B32104) 폼 / TASK_DATA / 템플릿 변수
 */
import type { RepeatingSectionRowData } from '@/features/AI-documents/types/docGen.types';

export interface WithdrawSecretaryFormModel {
  companyName: string;
  secretaryName: string;
  resignationDate: string;
  reasonForCessation: string;
  otherReasonForCessation: string;
  companyRegistrationNo: string;
  resolutionDate: string;
  secretaryIdentificationNo: string;
  companyAddress: string;
  /** 사직서(템플릿)용 주소 — Director 폼의 resigning director address 와 동일 역할 */
  secretaryResignationAddress: string;
  repeatingData: Record<string, RepeatingSectionRowData[]>;
  contactCd: string;
  positionCd: string;
  contactData: Record<string, any> | null;
}

export function createEmptyWithdrawSecretaryForm(): WithdrawSecretaryFormModel {
  return {
    companyName: '',
    secretaryName: '',
    resignationDate: '',
    reasonForCessation: '',
    otherReasonForCessation: '',
    companyRegistrationNo: '',
    resolutionDate: '',
    secretaryIdentificationNo: '',
    companyAddress: '',
    secretaryResignationAddress: '',
    repeatingData: {},
    contactCd: '',
    positionCd: 'B30033',
    contactData: null,
  };
}

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

/** BizFile / ACRA cessation — Others(B12004) → ceasedReason `OTH` (OfficerCessationForm.getEFilingData 와 동일) */
function ceasedReasonCodeForEfiling(reasonCd: string): string {
  const r = trim(reasonCd);
  if (r === 'B12003') return 'RESIGN';
  if (r === 'B12004') return 'OTH';
  return '';
}

function normalizeRepeatingData(
  repeatingData: Record<string, RepeatingSectionRowData[]> | null | undefined,
): Record<string, RepeatingSectionRowData[]> {
  const out: Record<string, RepeatingSectionRowData[]> = {};
  if (!repeatingData || typeof repeatingData !== 'object') return out;

  for (const [sectionName, rows] of Object.entries(repeatingData)) {
    if (!Array.isArray(rows)) continue;
    const cleanRows = rows
      .filter((row) => row && typeof row === 'object' && !Array.isArray(row))
      .map((row) => {
        const cleanRow: RepeatingSectionRowData = {};
        for (const [key, value] of Object.entries(row)) {
          cleanRow[key] = trim(value);
        }
        return cleanRow;
      });
    if (cleanRows.length > 0) out[sectionName] = cleanRows;
  }

  return out;
}

/** DR + Resignation Letter 템플릿 [[...]] 변수 (스네이크 케이스) */
export function toWithdrawSecretaryTemplateVariables(model: WithdrawSecretaryFormModel): Record<string, any> {
  const repeatingData = normalizeRepeatingData(model.repeatingData);
  return {
    company_name: trim(model.companyName),
    secretary_name: trim(model.secretaryName),
    resignation_date: trim(model.resignationDate),
    company_registration_no: trim(model.companyRegistrationNo),
    resolution_date: trim(model.resolutionDate),
    secretary_identification_no: trim(model.secretaryIdentificationNo),
    company_address: trim(model.companyAddress),
    secretary_resignation_address: trim(model.secretaryResignationAddress),
    reason_for_cessation: trim(model.reasonForCessation),
    other_reason_for_cessation: trim(model.otherReasonForCessation),
    reasonForCessation: trim(model.reasonForCessation),
    ceased_reason: ceasedReasonCodeForEfiling(trim(model.reasonForCessation)),
    ceasedReason: ceasedReasonCodeForEfiling(trim(model.reasonForCessation)),
    otherReason: trim(model.otherReasonForCessation),
    repeatingData,
    ...repeatingData,
    signers: '',
    Signers: '',
  };
}

export function isWithdrawSecretaryFormComplete(model: WithdrawSecretaryFormModel): boolean {
  const baseRequired = [
    model.companyName,
    model.secretaryName,
    model.resignationDate,
    model.companyRegistrationNo,
    model.resolutionDate,
    model.secretaryIdentificationNo,
    model.companyAddress,
    model.secretaryResignationAddress,
    model.contactCd,
    model.reasonForCessation,
  ].every((v) => trim(v).length > 0);

  if (!baseRequired) return false;
  if (trim(model.reasonForCessation) === 'B12004') {
    return trim(model.otherReasonForCessation).length > 0;
  }
  return true;
}

export function buildWithdrawSecretaryTaskFormData(
  model: WithdrawSecretaryFormModel,
  entityCd: string,
): Record<string, any> {
  const contactData = model.contactData ? { ...model.contactData } : null;
  const contactCd = trim(model.contactCd) || trim(contactData?.contactCd);
  const cessationDate = trim(model.resignationDate);
  const officerName = trim(model.secretaryName) || trim(contactData?.contactName);

  /** 사직일과 동일하게 넣으면 안 됨 — 연락처 조회에 온 실제 임명일만 TASK_DATA에 실음 */
  const appointmentFromContact = trim(
    contactData?.dateOfAppointment ??
      contactData?.dateOfAppointed ??
      contactData?.appointedDate ??
      contactData?.appointmentDate ??
      contactData?.appointmentEffectiveDate ??
      '',
  );

  const base: Record<string, any> = {
    entity: trim(entityCd),
    entityCd: trim(entityCd),
    entityName: { value: trim(model.companyName) },
    entityRegNo: { value: trim(model.companyRegistrationNo) },
    companyName: trim(model.companyName),
    companyRegistrationNo: trim(model.companyRegistrationNo),
    officerNameCd: contactCd,
    officerCode: contactCd,
    officerName,
    contactCd,
    position: trim(model.positionCd) || 'B30033',
    positionCd: trim(model.positionCd) || 'B30033',
    positionText: 'Secretary',
    secretaryName: trim(model.secretaryName),
    secretaryIdentificationNo: trim(model.secretaryIdentificationNo),
    companyAddress: trim(model.companyAddress),
    secretaryResignationAddress: trim(model.secretaryResignationAddress),
    resolutionDate: trim(model.resolutionDate),
    resignationDate: cessationDate,
    dateOfCessation: cessationDate,
    cessationDate,
    reasonForCessation: trim(model.reasonForCessation),
    ceasedReason: ceasedReasonCodeForEfiling(trim(model.reasonForCessation)),
    otherReason: trim(model.otherReasonForCessation),
    otherReasonForCessation: trim(model.otherReasonForCessation),
    repeatingData: normalizeRepeatingData(model.repeatingData),
    contactData,
  };

  if (appointmentFromContact) {
    base.appointedDate = appointmentFromContact;
    base.appointmentDate = appointmentFromContact;
    base.appointmentEffectiveDate = appointmentFromContact;
  }

  return base;
}
